// src/lib/posthog/friction.ts
//
// Pure decision rules for the two friction events (`form_error`,
// `feature_blocked`). No `window`, no React, no `capture` call: the six
// call sites (`permission-route-guard.tsx`, `onboarding-nag-dialog.tsx`,
// `withdraw-dialog.tsx`, `sign-up-form.tsx`, `2fa/verify/page.tsx`,
// `onboarding-wizard.tsx`) own the wiring and call these functions with
// plain values, so the decisions stay testable with literal tables —
// this repo's Vitest config runs with `environment: "node"` (no jsdom), so
// nothing inside a `.tsx` is reachable by a test.

import type { Requirement } from "@/lib/staff/nav-access";
import { invalidFieldPaths } from "./onboarding-events";

export type FormName = "signup" | "two_factor" | "kyb_wizard";

// A type alias, not an interface, on purpose: an interface has no implicit
// index signature, so it cannot be passed straight to `capture`'s
// `Record<string, unknown>` parameter and every call site would have to
// destructure it — which shadowed the react-hook-form `form` instance in two
// of them.
export type FormErrorProps = {
  form: FormName;
  field: string;
  error_code: string;
};

// `error_code` is react-hook-form's error `type` (the zod issue code), passed
// through when it matches this shape, `invalid` otherwise — so a zod
// `message` can never travel.
const ERROR_CODE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function readAt(errors: Record<string, unknown>, path: string): unknown {
  const segments = path.split(".");
  let node: unknown = errors;
  for (const segment of segments) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

// Never reads `message`, `ref` or `types` — only the leaf's `type`, guarded
// by ERROR_CODE_PATTERN so a free-text or non-string `type` can't travel.
function leafErrorCode(node: unknown): string {
  if (typeof node !== "object" || node === null) return "invalid";
  const type = (node as Record<string, unknown>).type;
  if (typeof type !== "string") return "invalid";
  return ERROR_CODE_PATTERN.test(type) ? type : "invalid";
}

/**
 * One `form_error` per invalid FIELD, from a react-hook-form `errors` tree.
 * Reuses `invalidFieldPaths` (`onboarding-events.ts`) for the paths instead
 * of walking the tree a second time, so the two modules can never disagree
 * on what a "field path" looks like.
 */
export function formErrorsFromTree(
  form: FormName,
  errors: Record<string, unknown>,
): FormErrorProps[] {
  return invalidFieldPaths(errors).map((field) => ({
    form,
    field,
    error_code: leafErrorCode(readAt(errors, field)),
  }));
}

/**
 * `/api/2fa/verify`'s HTTP status -> the `two_factor` form's error_code:
 * 400 or 401 -> `invalid_code`, 429 -> `rate_limited`,
 * anything else -> `unexpected`. The route's `error` string keeps being
 * shown to the user and is never captured.
 */
export function twoFactorErrorCode(status: number): string {
  if (status === 400 || status === 401) return "invalid_code";
  if (status === 429) return "rate_limited";
  return "unexpected";
}

/**
 * The route guard's `feature`, or `null` when the requirement cannot
 * block: `kind: "perm"` -> `<resource>_<action>`, `kind: "owner"`
 * -> `owner_only`, `kind: "any"` or `null` -> `null`.
 */
export function blockedFeature(req: Requirement | null): string | null {
  if (req === null) return null;
  if (req.kind === "perm") return `${req.resource}_${req.action}`;
  if (req.kind === "owner") return "owner_only";
  return null;
}
